/**
 * TrustGate Guided Request Wizard
 * 
 * BOUNDARY: UI for intent construction only
 * - No policy logic
 * - No authority decisions
 * - Calls createActionRequest via GuidedFlowEngine
 */

import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { GuidedFlowEngine } from './GuidedFlowEngine';
import { ProofStatusViewer } from './ProofStatusViewer';
import { WorkLifecycleNav, WorkLifecycleNextStep } from '../components/lifecycle/WorkLifecycleNav';
import { saveLifecycleContext } from '../components/lifecycle/WorkLifecycleContext';
import type { FlowPreset, FlowStep } from './types';

interface GuidedRequestWizardProps {
  preset: FlowPreset;
  userId: string;
  onComplete?: (requestId: string) => void;
  onCancel?: () => void;
}

export const GuidedRequestWizard: React.FC<GuidedRequestWizardProps> = ({
  preset,
  userId,
  onComplete,
  onCancel
}) => {
  const [engine] = useState(() => new GuidedFlowEngine(preset));
  const [currentStep, setCurrentStep] = useState<FlowStep | null>(null);
  const [state, setState] = useState(engine.getState());
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentStep(engine.getCurrentStep());
    setState(engine.getState());
  }, [engine]);

  const handleFieldChange = (fieldName: string, value: any) => {
    engine.updateField(fieldName, value);
    setState(engine.getState());
  };

  const handleNext = () => {
    if (engine.nextStep()) {
      setCurrentStep(engine.getCurrentStep());
      setState(engine.getState());
    }
  };

  const handlePrevious = () => {
    if (engine.previousStep()) {
      setCurrentStep(engine.getCurrentStep());
      setState(engine.getState());
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    
    try {
      const result = await engine.submit(userId);
      
      if (result.success && result.requestId) {
        setRequestId(result.requestId);
        setSubmitted(true);
        saveLifecycleContext({
          requestId: result.requestId,
          label: preset.name || 'Governed Request',
        });
        if (onComplete) {
          onComplete(result.requestId);
        }
      } else {
        alert(result.error || 'Failed to submit request');
      }
    } catch (error) {
      console.error('❌ Submit error:', error);
      alert('An unexpected error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && requestId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <h2 className="text-2xl font-bold text-white">Request Submitted</h2>
          </div>
          <p className="text-gray-400">
            Your request has been submitted to the governance system and is awaiting authority.
          </p>
        </div>

        {/* Lifecycle trail - shows where this request sits in the full lifecycle */}
        <div className="mb-6 p-4 bg-slate-900/60 border border-slate-800 rounded-lg">
          <p className="text-xs text-slate-500 mb-3">Work lifecycle</p>
          <WorkLifecycleNav
            currentStage="governed-request"
            context={{ requestId }}
            compact
          />
        </div>

        <ProofStatusViewer requestId={requestId} />

        <div className="mt-6 flex flex-wrap gap-3">
          <WorkLifecycleNextStep
            currentStage="governed-request"
            context={{ requestId }}
          />
          <button
            onClick={() => {
              engine.reset();
              setSubmitted(false);
              setRequestId(null);
              setCurrentStep(engine.getCurrentStep());
              setState(engine.getState());
            }}
            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
          >
            Submit Another Request
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!currentStep) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
        <p className="text-gray-400">No steps available for this flow.</p>
      </div>
    );
  }

  const metadata = engine.getFlowMetadata();
  const progress = engine.getProgress();
  const isLastStep = engine.isLastStep();
  const canProceed = engine.canProceed();

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">{metadata.name}</h1>
        <p className="text-gray-400">{metadata.description}</p>
        
        <div className="flex gap-2 mt-4">
          <span className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs rounded-full">
            {metadata.industry}
          </span>
          <span className="px-3 py-1 bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs rounded-full">
            {metadata.category}
          </span>
          {metadata.requiresAuthority && (
            <span className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 text-xs rounded-full">
              Requires Authority
            </span>
          )}
        </div>
      </div>

      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-gray-400">
            Step {state.currentStepIndex + 1} of {engine.getAllSteps().length}
          </span>
          <span className="text-sm text-gray-500">•</span>
          <span className="text-sm text-gray-400">{progress}% Complete</span>
        </div>
        <div className="w-full bg-gray-800 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-cyan-500 to-purple-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-white mb-2">
          {currentStep.label}
          {currentStep.required && <span className="text-red-400 ml-1">*</span>}
        </h2>
        <p className="text-gray-400 mb-6">{currentStep.description}</p>

        <div className="space-y-4">
          {renderField(
            currentStep,
            state.collectedParams[currentStep.fieldName],
            (value) => handleFieldChange(currentStep.fieldName, value),
            state.errors[currentStep.fieldName]
          )}
        </div>
      </div>

      <div className="flex gap-4">
        <button
          onClick={handlePrevious}
          disabled={state.currentStepIndex === 0}
          className="px-6 py-3 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          Previous
        </button>

        <div className="flex-1" />

        {!isLastStep ? (
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2 font-semibold"
          >
            Next
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={!canProceed || submitting}
            className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 disabled:from-gray-700 disabled:to-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2 font-semibold"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Submit Request
              </>
            )}
          </button>
        )}

        {onCancel && (
          <button
            onClick={onCancel}
            className="px-6 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {Object.keys(state.errors).length > 0 && (
        <div className="mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-red-300 mb-2">
                Please fix the following errors:
              </h3>
              <ul className="text-sm text-red-400 space-y-1">
                {Object.entries(state.errors).map(([field, error]) => (
                  <li key={field}>• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function renderField(
  step: FlowStep,
  value: any,
  onChange: (value: any) => void,
  error?: string
) {
  const baseInputClasses = `w-full px-4 py-3 bg-gray-800 border ${
    error ? 'border-red-500/50' : 'border-gray-700'
  } rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors`;

  switch (step.fieldType) {
    case 'select':
      return (
        <div>
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className={baseInputClasses}
          >
            <option value="">Select {step.label}...</option>
            {step.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div>
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={step.placeholder}
            rows={6}
            className={baseInputClasses}
          />
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
      );

    case 'json':
      return (
        <div>
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={step.placeholder}
            rows={8}
            className={`${baseInputClasses} font-mono text-sm`}
          />
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
      );

    case 'currency':
      return (
        <div>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={step.placeholder || '0.00'}
              className={`${baseInputClasses} pl-8`}
            />
          </div>
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
      );

    case 'number':
      return (
        <div>
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={step.placeholder}
            className={baseInputClasses}
          />
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
      );

    case 'text':
    default:
      return (
        <div>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={step.placeholder}
            className={baseInputClasses}
          />
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>
      );
  }
}
